<template>
  <div class="page-content">
    <div class="search-bar">
      <div class="search-input-wrapper">
        <i class="el-icon-search search-icon"></i>
        <input
          v-model="searchText"
          type="text"
          class="search-input"
          placeholder="搜索邀请码..."
          @keyup.enter="handleSearch"
        />
      </div>
      <div class="action-buttons">
        <button class="btn btn-secondary">
          <i class="el-icon-refresh"></i>
          刷新
        </button>
        <button class="btn btn-primary" @click="generateCodes">
          <i class="el-icon-plus"></i>
          生成邀请码
        </button>
      </div>
    </div>

    <div class="card">
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>邀请码</th>
              <th>状态</th>
              <th>使用者</th>
              <th>时间详情</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="code in invitations" :key="code.id" class="table-row">
              <td class="id-cell">#{{ code.id }}</td>
              <td>
                <div class="code-cell">
                  <code class="invite-code">{{ code.code }}</code>
                  <button class="copy-btn" title="复制" @click="copyCode(code.code)">
                    <i class="ri-file-copy-line"></i>
                  </button>
                </div>
              </td>
              <td>
                <span class="badge" :class="code.status === 'used' ? 'badge-used' : 'badge-unused'">
                  {{ code.status === 'used' ? '已使用' : '未使用' }}
                </span>
              </td>
              <td>
                <div class="used-by" v-if="code.usedByName">
                  <div class="user-avatar-small">
                    <i class="el-icon-user-solid"></i>
                  </div>
                  <span>{{ code.usedByName }}</span>
                </div>
                <span v-else class="text-muted">-</span>
              </td>
              <td>
                <div class="time-info">
                  <p class="time-created">创建: {{ code.createdTime || '-' }}</p>
                  <p class="time-used" v-if="code.usedTime">使用: {{ code.usedTime }}</p>
                </div>
              </td>
              <td>
                <div class="action-cell">
                  <button
                    v-if="code.status !== 'used'"
                    class="action-btn action-danger"
                    title="删除"
                    @click="deleteCode(code)"
                  >
                    <i class="ri-delete-bin-2-line"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="pagination-bar">
        <span class="pagination-info">显示 {{ (currentPage - 1) * pageSize + 1 }} 到 {{ Math.min(currentPage * pageSize, total) }} 条，共 {{ total }} 条</span>
        <div class="pagination">
          <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">上一页</button>
          <button
            v-for="page in visiblePages"
            :key="page"
            class="page-btn"
            :class="{ active: page === currentPage, ellipsis: page === '...' }"
            :disabled="page === '...'"
            @click="page !== '...' && (currentPage = page)"
          >
            {{ page }}
          </button>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="currentPage++">下一页</button>
        </div>
      </div>
    </div>

    <div v-if="showGenerateDialog" class="dialog-overlay" @click.self="showGenerateDialog = false">
      <div class="dialog">
        <div class="dialog-header">
          <h3>生成邀请码</h3>
          <button class="dialog-close" @click="showGenerateDialog = false">
            <i class="el-icon-close"></i>
          </button>
        </div>
        <div class="dialog-body">
          <div class="form-group">
            <label>生成数量</label>
            <input v-model.number="generateCount" type="number" min="1" max="100" class="form-input" />
          </div>
        </div>
        <div class="dialog-footer">
          <button class="btn btn-secondary" @click="showGenerateDialog = false">取消</button>
          <button class="btn btn-primary" @click="confirmGenerate">确认生成</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InvitationManagement',
  data() {
    return {
      invitations: [],
      searchText: '',
      currentPage: 1,
      pageSize: 10,
      total: 0,
      showGenerateDialog: false,
      generateCount: 10
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    },
    visiblePages() {
      const pages = []
      const total = this.totalPages
      const current = this.currentPage
      if (total <= 5) {
        for (let i = 1; i <= total; i++) pages.push(i)
      } else {
        if (current <= 3) {
          pages.push(1, 2, 3, 4, '...', total)
        } else if (current >= total - 2) {
          pages.push(1, '...', total - 3, total - 2, total - 1, total)
        } else {
          pages.push(1, '...', current - 1, current, current + 1, '...', total)
        }
      }
      return pages
    }
  },
  mounted() {
    this.loadInvitations()
  },
  watch: {
    currentPage() {
      this.loadInvitations()
    }
  },
  methods: {
    async loadInvitations() {
      try {
        const response = await this.$http({
          url: '/admin/user/invitation/list',
          method: 'get',
          params: {
            page: this.currentPage,
            size: this.pageSize,
            keyword: this.searchText
          }
        })
        this.invitations = response.codes || response || []
        this.total = this.invitations.length
      } catch (error) {
        this.$message.error('加载邀请码列表失败')
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.loadInvitations()
    },
    generateCodes() {
      this.showGenerateDialog = true
    },
    async confirmGenerate() {
      try {
        await this.$http({
          url: '/admin/user/invitation/create',
          method: 'post',
          data: {
            count: this.generateCount
          }
        })
        this.$message.success('邀请码生成成功')
        this.showGenerateDialog = false
        this.loadInvitations()
      } catch (error) {
        this.$message.error('生成失败')
      }
    },
    async deleteCode(code) {
      try {
        await this.$http({
          url: `/admin/user/invitation/delete/${code.id}`,
          method: 'post'
        })
        this.$message.success('删除成功')
        this.loadInvitations()
      } catch (error) {
        this.$message.error('删除失败')
      }
    },
    copyCode(code) {
      navigator.clipboard.writeText(code)
      this.$message.success('已复制到剪贴板')
    }
  }
}
</script>

<style scoped>
@import url('https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css');

.page-content {
  max-width: 100%;
}

.search-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
}

.search-input-wrapper {
  position: relative;
  max-width: 320px;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 16px;
}

.search-input {
  width: 100%;
  padding: 10px 12px 10px 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
  background: #fff;
}

.search-input:focus {
  border-color: #07c160;
  box-shadow: 0 0 0 3px rgba(7, 193, 96, 0.1);
}

.search-input::placeholder {
  color: #94a3b8;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn i {
  font-size: 16px;
}

.btn-secondary {
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #64748b;
}

.btn-secondary:hover {
  background: #f8fafc;
  border-color: #07c160;
  color: #07c160;
}

.btn-primary {
  background: #07c160;
  color: #fff;
  box-shadow: 0 2px 8px rgba(7, 193, 96, 0.25);
}

.btn-primary:hover {
  background: #06ae56;
  box-shadow: 0 4px 12px rgba(7, 193, 96, 0.35);
}

.card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
  max-height: calc(100vh - 380px);
  overflow-y: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.data-table thead {
  background: #f8fafc;
}

.data-table th {
  padding: 16px 24px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;
}

.data-table td {
  padding: 16px 24px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #334155;
}

.table-row:hover {
  background: #f9fafb;
}

.id-cell {
  color: #94a3b8;
  font-weight: 500;
}

.code-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.invite-code {
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 13px;
  color: #0f172a;
  font-family: 'Monaco', 'Consolas', monospace;
}

.copy-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.copy-btn:hover {
  background: #f1f5f9;
  color: #07c160;
}

.copy-btn i {
  font-size: 16px;
}

.badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
}

.badge-used {
  background: #ecfdf5;
  color: #059669;
}

.badge-unused {
  background: #fef3c7;
  color: #d97706;
}

.used-by {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-avatar-small {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 12px;
}

.text-muted {
  color: #94a3b8;
}

.time-info {
  display: flex;
  flex-direction: column;
}

.time-created {
  font-size: 12px;
  color: #64748b;
}

.time-used {
  font-size: 12px;
  color: #07c160;
}

.action-cell {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.action-btn i {
  font-size: 18px;
}

.action-btn:hover {
  background: #f1f5f9;
}

.action-danger:hover {
  color: #ef4444;
}

.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.pagination-info {
  font-size: 14px;
  color: #64748b;
}

.pagination {
  display: flex;
  gap: 4px;
}

.page-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  border-color: #07c160;
  color: #07c160;
}

.page-btn.active {
  background: #07c160;
  border-color: #07c160;
  color: #fff;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-btn.ellipsis {
  border: none;
  background: transparent;
  cursor: default;
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog {
  background: #fff;
  border-radius: 12px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
}

.dialog-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
}

.dialog-close {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s;
}

.dialog-close:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.dialog-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #334155;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
}

.form-input:focus {
  border-color: #07c160;
  box-shadow: 0 0 0 3px rgba(7, 193, 96, 0.1);
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
}
</style>